/** Server-owned service access and switches. Never initiates a purchase. */
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import UCSetup from "./UCSetup";

const ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 20000 });
const SERVICES = new Set(["accountant_support", "self_managed_tax", "universal_credit"]);

function Controls({ owner }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setRows(null); setError("");
    client.get(`${ROOT}/api/service-controls`, { signal: controller.signal }).then(({ data }) => {
      if (!Array.isArray(data?.services) || data.services.length !== 3
          || new Set(data.services.map(row => row.service)).size !== 3
          || data.services.some(row => !SERVICES.has(row.service) || typeof row.enabled !== "boolean"
            || !Number.isInteger(row.revision) || row.revision < 0)) throw new Error("Invalid controls");
      if (active) setRows(data.services);
    }).catch(() => { if (active) setError("Service settings could not be checked. Try again."); });
    return () => { active = false; controller.abort(); };
  }, [owner, retry]);

  const toggle = async row => {
    if (busy) return;
    setBusy(row.service); setError("");
    try {
      await client.put(`${ROOT}/api/service-controls/${row.service}`,
        { enabled: !row.enabled, expected_revision: row.revision },
        { headers: { "x-ndg-service-control": "browser-v1" } });
      setRetry(n => n + 1);
    } catch (_) {
      setError("The change was not confirmed. Refresh the settings before trying again.");
      // Do not optimistically flip or replay an uncertain write.
    } finally { setBusy(null); }
  };

  return <section aria-labelledby="service-controls-title" data-testid="service-controls" className="space-y-3">
    <h2 id="service-controls-title" className="font-bold text-slate-900 dark:text-white">Your services</h2>
    <p className="text-sm text-slate-600 dark:text-slate-300">Choose what Karen and the assistant can work on.</p>
    {error && <div role="alert"><p>{error}</p><button type="button" onClick={() => setRetry(n => n + 1)}>Refresh settings</button></div>}
    {!rows && !error && <p role="status">Checking your services…</p>}
    {rows?.map(row => {
      const canEnable = ["off", "setup_needed", "reconfirmation_needed", "active"].includes(row.state);
      return <div key={row.service} className="border-t border-slate-300 dark:border-slate-700 py-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-semibold">{row.label}</span>
          <button type="button" role="switch" aria-checked={row.enabled}
            aria-label={row.label} disabled={Boolean(busy) || (!row.enabled && !canEnable)}
            data-testid={`service-switch-${row.service}`} onClick={() => toggle(row)}
            className="min-h-11 px-4 py-2 border rounded disabled:opacity-50">
            {busy === row.service ? "Saving…" : row.enabled ? "On" : "Off"}
          </button>
        </div>
        <p className="text-sm" role="status">{row.message}</p>
        {row.max_mode === "assisted" && <p className="text-sm">Your access: assisted. Karen prepares work for your review.</p>}
        {row.max_mode === "autonomous" && <p className="text-sm">Your access: assistance and automation. Automation needs your settings and task permissions.</p>}
        {row.state === "upgrade_required" && <a href="/pricing" data-testid={`service-upgrade-${row.service}`}
          className="underline">Upgrade to enable</a>}
        {row.state === "setup_needed" && (row.service === "universal_credit"
          ? <UCSetup key={row.revision} onSaved={() => setRetry(n => n + 1)} />
          : <p className="text-sm">Setup questions are not available yet. This service remains paused.</p>)}
        {row.state === "reconfirmation_needed" && <p className="text-sm">Switch off and back on when you are ready to review setup.</p>}
      </div>;
    })}
  </section>;
}

export default function ServiceControls() {
  const { user, loading } = useAuth();
  const owner = String(user?.id || user?._id || "");
  if (loading || !owner) return null;
  if (!["adi", "pdi", "admin"].includes(user?.role)) return null;
  return <Controls key={owner} owner={owner} />;
}
